/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";
import { NumericFormat } from 'react-number-format';
import { Tooltip, OverlayTrigger} from "react-bootstrap";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../../../contexts/localeContext";
import { colors } from "../../../../constants/colors.js";
import { nameInitials, getCurrencySymbol } from "../../../../helpers/general";
import { useTravelCreditsService } from "../../../../services/travelCreditsService.js";
import { PAGE_TITLES } from "../../../../constants/appConstants";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useUserContext } from "../../../../contexts/userContext.js";
import { useHasPermission } from "../../../../hooks/useHasPermission";

/* Components */
import UCGrid from "../../../ucgrid/index.js";
import UCButton from "../../../buttons/index.js";
import GridNoData from "../../../gridNoData"
import GridSearchNoData from "../../../gridSearchNoData";
import PrimaryModal from "../../../modals/primaryModal/index.js";
import TravelCreditsAssignModal from "../../../modals/travelCreditsAssignModal/index.js";
import { ReactComponent as AssignIcon } from "../../../../resources/images/aa-users-plus.svg"
import { ReactComponent as CreditsIcon } from "../../../../resources/images/aa-credits-rounded.svg"
import { ReactComponent as HelpIcon } from "../../../../resources/images/help.svg";
import { ReactComponent as UnassignIcon } from "../../../../resources/images/user-x.svg";
import { ReactComponent as SearchIcon } from "../../../../resources/images/search-grid-rounded.svg";


/* Styles */
import styles from "./styles.js"

const AvailableGrid = ({gridOptions, setGridOptions, query, clearSearch, setEnableDownloadButton}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const userDetails = useUserContext();
  const navigate = useNavigate();
  const globalLocale = useLocaleContext();
  const { getTravelCreditsList, travelCreditsResponse, unassignTripCredit, assignTravelCredit } = useTravelCreditsService();
  const { initAnalytics } = useAnalytics();
  const { checkPermission } = useHasPermission();

  const [selectedRow, setSelectedRow] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["availableCredits"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, []);

  useEffect(() => {
    getTravelCreditsList(gridOptions, "AVAILABLE" , "availableGridSpinner");
    gridOptions.page > 1 ? navigate(`/app/travelcredits/available?page=${gridOptions.page}`) : navigate(`/app/travelcredits/available`)
  }, [gridOptions]);

  useEffect(() => {
    if(travelCreditsResponse?.results?.dataItems.length > 0) {
      setEnableDownloadButton(true)
    } else {
      setEnableDownloadButton(false)
    }
  }, [travelCreditsResponse]);

  const actionCallback = (actionType) => {
    switch (actionType) {
      case "assign":
        setShowAssignModal(true)
        break;
      case "unassign":
        setShowUnassignModal(true)
        break;
    }
  }

  const unassignAction = async () => {
    const response = await unassignTripCredit(selectedRow?.fopId)
    if (response) {
      setShowUnassignModal(false)
      getTravelCreditsList(gridOptions, "AVAILABLE" , "availableGridSpinner");
    }
  }

  const assignAction = async (groupData, setShowTravelCreditsAssignModal) => {
    const response = await assignTravelCredit(groupData)

    if (response) {
      setShowTravelCreditsAssignModal(false)
      getTravelCreditsList(gridOptions, "AVAILABLE" , "availableGridSpinner");
    }
  }

  const displayShowAssignModal = async (row) => {
    setShowAssignModal(true)
    setSelectedRow(row)
  }

  const onRowClick = row => {}

  return (
    <>
      <UCGrid
        gridStyle={classes.availableGrid}
        gridWrapperClassName={classes.availableGrid}
        gridName="availableGrid"
        data={ travelCreditsResponse?.results }
        columns={ travelCreditsResponse?.columns }
        actions={ travelCreditsResponse?.actions }
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/travelcredits/available?page=${page}`) : navigate(`/app/travelcredits/available`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner="availableGridSpinner"
        customColumns={{
          addedDate: (row) => (
            <div className={classes.link}>{ moment(row?.addedDate).locale(globalLocale).format("D MMM YYYY") }</div>
          ),
          assignedTo: (row) => (
            <div>
            {
              row?.assignedTo ? 
              <div className={classes.link}>
                <div className={classes.nameWrapper}>
                  <div className={classes.initialsAvatar}>
                    <span>{ nameInitials(row?.assignedTo) }</span>
                  </div>
                  <div className={classes.details}>
                    <p>{row?.assignedTo}</p>
                    <p>{ row?.email ? row.email : "-" }</p>
                  </div>
                </div>
              </div>
              :
              checkPermission("F_TRIPCREDIT_ASSIGN") &&
              <UCButton
                type="secondary"
                className={classes.assignBtn}
                text={<FormattedMessage id="paymentsGrid.assignButton"/>}
                onClick={() => displayShowAssignModal(row)}
                loadingStateStyling={classes.assingBtnLoadingState}
                spinner={{
                  name: `assignTeamsButton${row.entityID}`,
                  size: 20
                }}
              />
            }
            </div>
          ),
          value: (row) => {
          return (
            <NumericFormat
              value={row?.value}
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
              prefix={getCurrencySymbol(row?.currency)}
              renderText={(value) => <div>{value} {row?.currency}</div>}
            />
           )
          },
          expiryDate: (row) => {
            const isExpiringSoon = moment(row?.expiryDate).diff(moment(), 'days') >= 0 && moment(row?.expiryDate).diff(moment(), 'days') < 90;
            return (
              <div className={isExpiringSoon ? classes.expiringSoon : classes.number}> {moment(row?.expiryDate).locale(globalLocale).format("DD MMM YYYY") } </div>
            )
          },
          issueDateTime: (row) => (
            <div className={classes.link}>
              {row?.issueDateTime && row.issueDateTime !== "0000-00-00 00:00:00"
                ? moment(row.issueDateTime).locale(globalLocale).format("DD MMM YYYY")
                : "-"
              }
            </div>
          ),
        }}
        columnCustomWidth={{
          number: "200px !important",
          ticketNumber: "200px !important",
          value: "250px !important",
          assignedTo: "320px !important",
          actions: "50px !important",
          expiryDate: "200px !important",
          originalTravelerName: "250px !important"
        }}
        columnCustomProps={[
          {
            columnName: "assignedTo",
            tooltip: true,
            component: (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip" className={classes.tooltip}>
                    <FormattedMessage id="availableGrid.tooltipText"/>
                  </Tooltip>
                }
              >
                <HelpIcon className={classes.helpIcon}/>
              </OverlayTrigger>
            )
          },
          {
            columnName: "originalTravelerName",
            tooltip: true,
            component: (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="originalTravelerNameTooltip" className={classes.tooltip}>
                    <FormattedMessage id="tripCredit.previousTravelerToolTip" />
                  </Tooltip>
                }
              >
                <HelpIcon className={classes.helpIcon}/>
              </OverlayTrigger>
            )
          },
          {
            columnName: "ticketNumber",
            tooltip: true,
            component: (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip" className={classes.tooltip}>
                    <FormattedMessage id="tripCredit.ticketToolTip"/>
                  </Tooltip>
                }
              >
                <HelpIcon className={classes.helpIcon}/>
              </OverlayTrigger>
            )
          },
        ]}
        noDataComponent={ 
          query ?
            <GridSearchNoData 
              icon={<SearchIcon />} 
              title={<FormattedMessage id="travelcredits.search.noResults"/>} 
              buttonText={<FormattedMessage id="travelcredits.search.clear"/>}
              clearSearch={clearSearch}
              spinner="availableGridSpinner"
            /> 
          :
            <GridNoData 
              icon={<CreditsIcon />} 
              title={<FormattedMessage id="availableGrid.noDataTitle"/>} 
              description={<FormattedMessage id="availableGrid.noDataDescription"/>}
            /> 
        }
        onRowClick={onRowClick}
        defaultSort={travelCreditsResponse?.defaultSort}
        handleSort={(column, sortDirection) =>
          setGridOptions({
            ...gridOptions,
            sortby: column.id,
            sortdirection: sortDirection === "desc" ? "dsc" : sortDirection,
          })
        }
      />
        {
          travelCreditsResponse?.actions && travelCreditsResponse.actions.map((action,i) => {
          switch (action) {
            case "assign":
            return <React.Fragment key={`${action}-${i}`}>
              <PrimaryModal
                showModal={showUnassignModal}
                setShowModal={setShowUnassignModal}
                setSelectedRow={setSelectedRow}
                modalIcon={ <UnassignIcon /> }
                modalTitle={ <FormattedMessage id="availableGrid.unassignActionTitle" />  }
                modalDescription={ <FormattedMessage id="availableGrid.unassignActionDescription" values={{ticketNumber: selectedRow?.number}}/> }
                saveButtonText={<FormattedMessage id="availableGrid.unassignAction.unassign"/>}
                submitCallback={unassignAction}
                dataTestId={"unassignactionmodal"}
                spinnerName={"unassignActionSpinner"}
              />
              <TravelCreditsAssignModal 
                showTravelCreditsAssignModal={showAssignModal}
                setShowTravelCreditsAssignModal={setShowAssignModal}
                selectedRow={selectedRow}
                userDetails={userDetails}
                submitCallback={assignAction}
              />
            </React.Fragment>
          }
        })
      }
    </>
  )
}

export default AvailableGrid;